import * as React from "react";
import { useState, useRef, useEffect, useMemo, useCallback, useReducer } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Loader2, AlertCircle, AlertTriangle, MoreHorizontal, ChevronDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

// Debounce hook for search optimization
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);
  
  return debouncedValue;
}

// Component link interface - matches the schema in mcmService.ts
interface ComponentLink {
  id: string;              // canonical identifier in the format <scheme>[:<version>]:<ref>
  domain: string;          // classification (Asset, Policy, Risk, Control, etc.)
  scheme: string;          // namespace (internal, iso27001:2013, nist800-53r5, etc.)
  ref: string;             // raw identifier inside the scheme
  title?: string;          // human-readable label (optional)
  relation: string;        // relationship (implements, maps-to, mitigates, etc.)
  confidence: number;      // number between 0-1 for mapping certainty
}
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BulkOperation, MCMService } from '@/services/mcmService';
import { toast } from 'sonner';
import * as assetsService from '@/services/assetsService';
import * as documentsService from '@/services/documentsService';
import * as risksService from '@/services/risksService';
import * as controlsService from '@/services/controlsService';
import * as assuranceActionsService from '@/services/assuranceActionsService';
import { getPolicyList } from '@/services/policyService';
import { useTenancy } from '@/contexts/TenancyContext';

// Tab configuration with types
type TabId = 'assets' | 'assurances' | 'documents' | 'policies' | 'risks' | 'controls' | 'linkedComponents';

interface TabItem {
  id: string;
  name: string;
  isLinked?: boolean; // Flag to indicate if this item is already linked
  linkDetails?: ComponentLink; // Optional details about existing link
}

interface TabConfig {
  label: string;
  placeholder: string;
  items: TabItem[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  page: number;
}

const MAX_TABS = 4;
const TAB_IDS: TabId[] = ['assets', 'assurances', 'documents', 'policies', 'risks', 'controls', 'linkedComponents'];

const getTabLabel = (id: TabId): string => {
  switch (id) {
    case 'assets': return 'Assets';
    case 'assurances': return 'Assurances';
    case 'documents': return 'Documents';
    case 'policies': return 'Policies';
    case 'risks': return 'Risks';
    case 'controls': return 'Controls';
    case 'linkedComponents': return 'Linked Components';
    default: return id;
  }
};

const getTabPlaceholder = (id: TabId): string => {
  return `Search ${id.toLowerCase()}...`;
};


interface BulkOperationsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (operation: Omit<BulkOperation, 'controlIds'>) => Promise<void>;
  selectedCount: number;
  selectedControlIds?: string[];
  isLoading?: boolean;
}

export default function BulkOperationsModal({
  isOpen,
  onClose,
  onSubmit,
  selectedCount,
  selectedControlIds = [],
  isLoading = false,
}: BulkOperationsModalProps) {
  const { activeTenancy } = useTenancy();
  const [activeTab, setActiveTab] = useState<TabId>('assets');
  const [selectedItems, setSelectedItems] = useState<Record<string, boolean>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [linkFilter, setLinkFilter] = useState<'all' | 'linked' | 'unlinked'>('all');
  const [searchActive, setSearchActive] = useState(false);
  
  // Update searchActive whenever searchQuery changes
  useEffect(() => {
    setSearchActive(searchQuery.trim().length >= 2);
  }, [searchQuery]);
  const debouncedSearchQuery = useDebounce(searchQuery, 350); // 350ms delay
  const [tabs, setTabs] = useState<Record<TabId, TabConfig>>(
    TAB_IDS.reduce((acc, id) => ({
      ...acc,
      [id]: {
        label: getTabLabel(id),
        placeholder: getTabPlaceholder(id),
        items: [],
        loading: true,
        error: null,
        hasMore: true,
        page: 0,
      },
    }), {} as Record<TabId, TabConfig>)
  );

  // Track loaded tabs with useRef to prevent re-renders
  const loadedTabsRef = useRef<Record<TabId, boolean>>(
    TAB_IDS.reduce((acc, id) => ({ ...acc, [id]: false }), {} as Record<TabId, boolean>)
  );

  // Track loading state with ref to prevent re-renders
  const loadingRef = useRef<Record<TabId, boolean>>(
    TAB_IDS.reduce((acc, id) => ({ ...acc, [id]: false }), {} as Record<TabId, boolean>)
  );

  // Store selectedControlIds in a ref to persist across re-renders
  const selectedControlIdsRef = useRef<string[]>([]);
  
  // Initialize the ref with current props value
  useEffect(() => {
    selectedControlIdsRef.current = [...selectedControlIds];
  }, [selectedControlIds]);

  
  // PAGE_SIZE is used for chunked loading
  const PAGE_SIZE = 50;
  
  // Function to fetch data for a tab with pagination
  const fetchTabData = useCallback(async (tabId: TabId, page: number = 0, append: boolean = false, fetchAll: boolean = false) => {
    // Enhanced logging to help diagnose the issue
    console.log(`fetchTabData called: tabId=${tabId}, page=${page}, append=${append}, fetchAll=${fetchAll}, loading=${loadingRef.current[tabId]}`);
    console.log('Current tabs data:', tabs);
    
    // Prevent duplicate requests
    if (loadingRef.current[tabId]) {
      console.log(`${tabId} data fetch skipped - already loading`);
      return;
    }
    
    console.log(`Fetching ${tabId} data [page=${page}, append=${append}, fetchAll=${fetchAll}]`);
    
    try {
      // Run pre-fetch routine (capture focus state)
      beforeFetchRef.current();
      
      // Mark as loading - both in ref and state
      loadingRef.current[tabId] = true;
      
      // Always update state immediately to show loading indicator
      setTabs(prev => {
        console.log('Setting tabs loading state for', tabId);
        return {
          ...prev,
          [tabId]: { ...prev[tabId], loading: true, error: null }
        };
      });
      
      const limit = fetchAll ? undefined : PAGE_SIZE;
      const offset = fetchAll ? 0 : page * PAGE_SIZE;

      let items: TabItem[] = [];
      const companyID = (window as any).companyID || '';

      let hasMore = !fetchAll;
      let allData: any[] = [];
      
      // Get the control data for checking linked components
      let controlData: any = null;
      let existingLinks: ComponentLink[] = [];
      
      if (selectedControlIdsRef.current && selectedControlIdsRef.current.length > 0) {
        try {
          // Fetch the first selected control to check for linked components
          controlData = await MCMService.getControlById(
            selectedControlIdsRef.current[0],
            activeTenancy?.id // Pass tenancyID to get customer-specific data
          );
          
          // Extract existing linked components for checking
          existingLinks = controlData?.linkedComponents || [];
          console.log('Existing links found:', existingLinks.length);
        } catch (err) {
          console.warn('Failed to fetch control data for link checking', err);
        }
      }
      
      // Helper function to check if an item is already linked
      const isItemLinked = (itemId: string, domain: string): { isLinked: boolean, linkDetails?: ComponentLink } => {
        if (!existingLinks.length) return { isLinked: false };
        
        const link = existingLinks.find(link => {
          const cleanItemId = itemId.trim();
          const cleanLinkRef = link.ref.trim();
          const domainMatches = link.domain.toLowerCase() === domain.toLowerCase();
          const idMatches = cleanLinkRef === cleanItemId;
          return domainMatches && idMatches;
        });
        
        return link ? { isLinked: true, linkDetails: link } : { isLinked: false };
      };
        
      switch (tabId) {
          case 'assets': {
            const assets = await assetsService.getAssets();
            allData = assets || [];
            // Apply pagination if not fetching all
            const paginatedAssets = fetchAll ? assets : (assets?.slice(offset, offset + limit) || []);
            hasMore = !fetchAll && assets.length > offset + paginatedAssets.length;
            items = paginatedAssets.map(asset => {
              const assetData = asset as any; // Temporary type assertion
              const id = assetData.componentId || assetData.id || '';
              const linkCheck = isItemLinked(id, 'Asset');
              return {
                id,
                name: assetData.name || assetData.assetDesc || assetData.componentName || 'Unnamed Asset',
                isLinked: linkCheck.isLinked,
                linkDetails: linkCheck.linkDetails
              };
            });
            break;
          }
          case 'assurances': {
            const assuranceTasks = await assuranceActionsService.getAllTasks(companyID);
            allData = assuranceTasks || [];
            // Apply pagination if not fetching all
            const paginatedAssurances = fetchAll ? assuranceTasks : (assuranceTasks?.slice(offset, offset + limit) || []);
            hasMore = !fetchAll && assuranceTasks.length > offset + paginatedAssurances.length;
            items = paginatedAssurances.map(task => {
              const id = task.assID || '';
              const linkCheck = isItemLinked(id, 'Assurance');
              return {
                id,
                name: task.checkDetail?.check || 'Unnamed Assurance Task',
                isLinked: linkCheck.isLinked,
                linkDetails: linkCheck.linkDetails
              };
            });
            break;
          }
          case 'documents': {
            const docs = await documentsService.getDocuments(companyID);
            allData = docs || [];
            // Apply pagination if not fetching all
            const paginatedDocs = fetchAll ? docs : (docs?.slice(offset, offset + limit) || []);
            hasMore = !fetchAll && docs.length > offset + paginatedDocs.length;
            items = paginatedDocs.map(doc => {
              const id = doc.docID || doc.id || '';
              const linkCheck = isItemLinked(id, 'Document');
              return {
                id,
                name: doc.name || doc.fileName || 'Unnamed Document',
                isLinked: linkCheck.isLinked,
                linkDetails: linkCheck.linkDetails
              };
            });
            break;
          }
          case 'policies': {
            const policies = await getPolicyList(companyID);
            allData = policies || [];
            // Apply pagination if not fetching all
            const paginatedPolicies = fetchAll ? policies : (policies?.slice(offset, offset + limit) || []);
            hasMore = !fetchAll && policies.length > offset + paginatedPolicies.length;
            items = paginatedPolicies.map(policy => {
              const id = policy.id || '';
              const linkCheck = isItemLinked(id, 'Policy');
              return {
                id,
                name: policy.name || 'Unnamed Policy', // Fixed policy_name not existing error
                isLinked: linkCheck.isLinked,
                linkDetails: linkCheck.linkDetails
              };
            });
            break;
          }
          case 'risks': {
            const risks = await risksService.getAllRisks(companyID);
            allData = risks || [];
            // Apply pagination if not fetching all
            const paginatedRisks = fetchAll ? risks : (risks?.slice(offset, offset + limit) || []);
            hasMore = !fetchAll && risks.length > offset + paginatedRisks.length;
            items = paginatedRisks.map(risk => {
              const id = risk.id || '';
              const linkCheck = isItemLinked(id, 'Risk');
              return {
                id,
                name: risk.risk || 'Unnamed Risk',
                isLinked: linkCheck.isLinked,
                linkDetails: linkCheck.linkDetails
              };
            });
            break;
          }
          case 'controls': {
            const controls = await controlsService.getAllControls(() => true);
            allData = controls || [];
            // Apply pagination if not fetching all
            const paginatedControls = fetchAll ? controls : (controls?.slice(offset, offset + limit) || []);
            hasMore = !fetchAll && controls.length > offset + paginatedControls.length;
            items = paginatedControls.map(control => {
              const id = control.value || '';
              const linkCheck = isItemLinked(id, 'Control');
              return {
                id,
                name: control.label || 'Unnamed Control',
                isLinked: linkCheck.isLinked,
                linkDetails: linkCheck.linkDetails
              };
            });
            break;
          }
          case 'linkedComponents': {
            // Get linked components from the stored ref of selected control IDs
            // Use the ref instead of the prop to ensure we have the latest values
            console.log(`linkedComponents case entered with IDs:`, selectedControlIdsRef.current);
            
            if (!selectedControlIdsRef.current || selectedControlIdsRef.current.length === 0) {
              console.log('⚠️ No selected control IDs available for linkedComponents');
              items = [];
              hasMore = false;
              allData = [];
              break;
            }
            
            try {
              console.log(`Fetching linked components for ${selectedControlIdsRef.current.length} controls, first ID: ${selectedControlIdsRef.current[0]}`);
              
              // Always fetch fresh data directly from the service
              // This ensures we don't have stale data issues after hot reloads
              const controlData = await MCMService.getControlById(
                selectedControlIdsRef.current[0],
                activeTenancy?.id // Pass tenancyID to get customer-specific data
              );
              
              console.log('Control data received:', controlData);
              console.log('Linked components count:', controlData?.linkedComponents?.length || 0);
              console.log('Related assurance tasks count:', controlData?.relatedAssuranceTasks?.length || 0);
              
              if (controlData) {
                // Combine linkedComponents and relatedAssuranceTasks into a single array
                const linkedComponents = Array.isArray(controlData.linkedComponents) ? controlData.linkedComponents : [];
                const relatedAssuranceTasks = Array.isArray(controlData.relatedAssuranceTasks) ? controlData.relatedAssuranceTasks : [];
                
                // Transform relatedAssuranceTasks to match the ComponentLink structure
                // Create promises array for all task fetching operations
                const taskPromises = relatedAssuranceTasks.map(async task => {
                  console.log('Processing task:', task)
                  if (!task) return null;
                  
                  try {
                    // Fetch assurance task details
                    const assuranceTask = await assuranceActionsService.getTask(task);
                    if (!assuranceTask) {
                      console.log('Task not found:', task);
                      return null;
                    }
                    
                    return {
                      id: assuranceTask?.assID || task || '',
                      domain: 'Assurance',
                      scheme: 'internal',
                      ref: assuranceTask?.assID || task || '',
                      title: assuranceTask?.checkDetail?.check || assuranceTask?.id || assuranceTask?.assID || task || 'Assurance Task',
                      relation: 'assures',
                      confidence: 0.9
                    };
                  } catch (error) {
                    console.error('Error processing assurance task:', error);
                    return null;
                  }
                });
                
                // Wait for all promises to resolve and filter out null results
                const assuranceTaskLinks = (await Promise.all(taskPromises)).filter(link => link !== null);
                console.log('Processed assurance tasks:', assuranceTaskLinks);
                
                // Combine both arrays
                const combinedComponents = [...linkedComponents, ...assuranceTaskLinks];
                allData = combinedComponents;
                
                // Apply pagination to combined array
                const paginatedComponents = fetchAll 
                  ? combinedComponents
                  : (combinedComponents.slice(offset, offset + limit) || []);
                
                hasMore = !fetchAll && combinedComponents.length > offset + paginatedComponents.length;
                
                // Prepare array to hold processed items with titles from services if needed
                const processedItems = [];
                
                // Process each link and fetch details if title is missing
                for (const link of paginatedComponents) {
                  const id = link.ref || link.id || '';
                  let name = link.title || '';
                  
                  // If title is missing, fetch from appropriate service based on domain
                  if ((!name || name === '') && id && link.domain) {
                    try {
                      // Determine which service to use based on domain
                      switch(link.domain.toLowerCase()) {
                        case 'asset':
                          try {
                            const asset = await assetsService.getAsset(id);
                            console.log('Asset fetched:', asset);
                            // The exact structure may vary, try different possible property paths
                            if (asset) {
                              // Try various property paths where the name might be stored
                              name = id; // Default fallback
                              
                              // Type casting to access properties safely
                              const assetAny = asset as any;
                              if (assetAny.name) name = assetAny.name || assetAny.assetName || assetAny.assetDesc;
                              else if (assetAny.assetName) name = assetAny.assetName || assetAny.assetDesc;
                              else if (assetAny.assetDesc) name = assetAny.assetDesc;
                              else if (assetAny.entry?.assetName) name = assetAny.entry.assetName || assetAny.entry.assetDesc;
                              else if (assetAny.entry?.name) name = assetAny.entry.name || assetAny.entry.assetDesc;
                              else if (assetAny.entry?.assetDesc) name = assetAny.entry.assetDesc;
                              else if (assetAny.details?.name) name = assetAny.details.name || assetAny.details.assetDesc;
                              else if (assetAny.details?.assetDesc) name = assetAny.details.assetDesc;
                              else if (assetAny.data.assetDesc) name = assetAny.data.assetDesc;
                            }
                          } catch (assetError) {
                            console.warn(`Error fetching asset ${id}:`, assetError);
                            name = `Asset ${id}`;
                          }
                          break;
                        case 'assurances':
                        case 'assurance':
                          // Try to use existing data first if this is from relatedAssuranceTasks
                          if (link.title) {
                            name = link.title;
                          } else {
                            const assuranceTasks = await assuranceActionsService.getAllTasks(activeTenancy?.id || '');
                            const assuranceTask = assuranceTasks.find(t => t.assID === id);
                            if (assuranceTask) name = assuranceTask.checkDetail?.check || id;
                            else name = `Assurance Task ${id}`;
                          }
                          break;
                          
                        case 'policy':
                          const policies = await getPolicyList(activeTenancy?.id || '');
                          const policy = policies.find(p => p.id === id);
                          if (policy) name = policy.name || (policy as any).policy_name || id;
                          break;
                          
                        case 'risk':
                          if (activeTenancy?.id) {
                            const risk = await risksService.getRisk(activeTenancy.id, id);
                            console.log('Risk fetched:', risk);
                            if (risk.risk) name = risk.risk;
                            else if (risk?.description) name = risk.description;
                            else if (risk?.entry?.risk) name = risk.entry.risk;
                            else if (risk?.entry?.description) name = risk.entry.description;
                            else if (risk?.details?.risk) name = risk.details.risk;
                            else if (risk?.details?.description) name = risk.details.description;
                            else name = id;
                          }
                          break;
                          
                        case 'control':
                          // Use MCMService directly since we need specific control
                          const control = await MCMService.getControlById(id, activeTenancy?.id);
                          if (control) name = control.controlName || control.name || id;
                          break;
                          
                        case 'document':
                          const doc = await documentsService.getDocument(id, activeTenancy?.id || null);
                          if (doc) name = doc.name || doc.title || id;
                          break;
                          
                        default:
                          // If domain is unknown or not handled, use ID as fallback
                          name = `${link.domain}: ${id}`;
                      }
                    } catch (serviceError) {
                      console.warn(`Error fetching details for ${link.domain} with ID ${id}:`, serviceError);
                      name = `${link.domain} ${id}`; // Fallback with domain and ID
                    }
                  }
                  
                  // Add to processed items
                  processedItems.push({
                    id,
                    name: name || 'Unnamed Component',
                    domain: link.domain || 'Unknown',
                    relation: link.relation || 'related-to',
                    // Flag to indicate if this is from relatedAssuranceTasks
                    isAssuranceTask: link.domain?.toLowerCase() === 'assurance' || link.relation === 'assures'
                  });
                }
                
                console.log('Processed items before assignment:', processedItems);
                items = processedItems;
                console.log('Final items array after assignment:', items);
              } else {
                console.log('No linked components found or not an array');
                items = [];
                hasMore = false;
              }
            } catch (error) {
              console.error('Error fetching linked components:', error);
              items = [];
              hasMore = false;
            }
            console.log('End of linkedComponents case - items:', items, 'hasMore:', hasMore);
            break;
          }
        }

        // Update data
        loadingRef.current[tabId] = false;
        if (page === 0) {
          loadedTabsRef.current[tabId] = true;
        }
        
        setTabs(prev => ({
          ...prev,
          [tabId]: {
            ...prev[tabId],
            // If appending, add to existing items, otherwise replace
            items: append ? [...prev[tabId].items, ...items] : items,
            loading: false,
            error: null,
            hasMore,
            page,
            totalCount: allData.length
          },
        }));
    } catch (error) {
      console.error(`Error fetching ${tabId}:`, error);
      loadingRef.current[tabId] = false;
      setTabs(prev => ({
        ...prev,
        [tabId]: {
          ...prev[tabId],
          loading: false,
          error: `Failed to load ${tabId}. Please try again.`,
        },
      }));
    }
  }, []);

  // Initial data loading when tab changes
  useEffect(() => {
    const tabId = activeTab;
    console.log(`Tab changed to ${tabId}, selectedControlIds:`, selectedControlIds);
    
    if (tabId === 'linkedComponents') {
      // Always force reload linkedComponents tab data when selected to prevent race conditions
      console.log(`Force reloading linkedComponents tab data with ${selectedControlIds.length} selected controls`);
      // Store selectedControlIds in a ref to ensure the closure in fetchTabData has access to the latest values
      selectedControlIdsRef.current = [...selectedControlIds];
      loadedTabsRef.current[tabId] = false;
      loadingRef.current[tabId] = false;
      fetchTabData(tabId, 0, false);
    } else if (!loadedTabsRef.current[tabId] && !loadingRef.current[tabId]) {
      // For other tabs, only load if not already loaded
      console.log(`Loading ${tabId} tab data on tab change (regular tab)`); 
      fetchTabData(tabId, 0, false);
    }
  }, [activeTab, fetchTabData, selectedControlIds]);

  // Monitor selectedControlIds changes and reload linkedComponents if needed
  useEffect(() => {
    // Only reload if we're currently on the linkedComponents tab and have selected control IDs
    if (activeTab === 'linkedComponents') {
      // Perform additional logging to track the issue
      console.log('selectedControlIds changed while on linkedComponents tab:', selectedControlIds);
      console.log('Current ref value:', selectedControlIdsRef.current);
      
      // Always update the ref with the latest prop value
      selectedControlIdsRef.current = [...selectedControlIds];
      
      // Force reload data - even if the array is empty, we want to show the empty state
      loadedTabsRef.current[activeTab] = false;
      loadingRef.current[activeTab] = false;
      fetchTabData(activeTab, 0, false);
    }
  }, [selectedControlIds, activeTab, fetchTabData]);

  // Effect to set search active status and handle search clearing
  useEffect(() => {
    const isSearchActive = debouncedSearchQuery.length >= 2;
    setSearchActive(isSearchActive);
    
    // When search is cleared (previously active, now inactive)
    if (!isSearchActive && searchActive) {
      // Reset to initial paginated view for current tab
      // We need to force a data reset to avoid keeping all items loaded
      const currentTabId = activeTab;
      setTabs(prev => ({
        ...prev,
        [currentTabId]: {
          ...prev[currentTabId],
          items: prev[currentTabId].items.slice(0, PAGE_SIZE), // Keep only first page
          hasMore: true, // Reset hasMore flag
          page: 0 // Reset page count
        }
      }));
    }
  }, [debouncedSearchQuery, searchActive, activeTab]);

  // Create a forceUpdate function using reducer
  const [, forceUpdate] = useReducer(x => x + 1, 0);
  
  // Keep track of search input element
  const searchInputRef = useRef<HTMLInputElement>(null);
  
  // Flag to track if input was focused before data loading
  const wasFocusedRef = useRef(false);
  
  // Track the active element to detect focus changes
  const activeElementRef = useRef<Element | null>(null);
  
  // When loading status changes, handle focus management
  useEffect(() => {
    const activeTabConfig = tabs[activeTab];
    const isLoading = activeTabConfig?.loading;
    
    // Only try to restore focus when loading completes
    if (!isLoading && wasFocusedRef.current) {
      // Use multiple attempts with increasing delays for more reliability
      const attemptFocus = (attempt = 0) => {
        if (searchInputRef.current && document.activeElement !== searchInputRef.current) {
          searchInputRef.current.focus();
          
          // If we're still not focused after the attempt, try again with exponential backoff
          if (document.activeElement !== searchInputRef.current && attempt < 3) {
            setTimeout(() => attemptFocus(attempt + 1), 50 * Math.pow(2, attempt));
          } else {
            // We've either succeeded or given up after 3 attempts
            wasFocusedRef.current = false;
          }
        } else {
          // Either already focused or ref is null
          wasFocusedRef.current = false;
        }
      };
      
      // Start the focus attempts
      setTimeout(attemptFocus, 0);
    }
  }, [tabs, activeTab]);
  
  // Special effect that runs before each tab data fetch
  const beforeFetchRef = useRef<() => void>(() => {});
  beforeFetchRef.current = () => {
    // Save focus state and the active element before fetch
    activeElementRef.current = document.activeElement;
    wasFocusedRef.current = document.activeElement === searchInputRef.current;
    
    // If this is a data reload after clearing search, we want to preserve the paginated nature
    if (searchQuery === '') {
      // We'll only load one page of data when search is cleared
      loadedTabsRef.current[activeTab] = false;
    }
  };

  // Search effect to fetch all data when searching (with debounce)
  useEffect(() => {
    // Only fetch all items when searching with at least 2 characters
    if (debouncedSearchQuery && debouncedSearchQuery.length >= 2) {
      const tabId = activeTab;
      fetchTabData(tabId, 0, false, true); // Fetch all data (no pagination)
    }
    // Note: We don't reset data here when search is cleared - that's handled in the search active effect
  }, [debouncedSearchQuery, activeTab, fetchTabData]);

  // Filter items based on search query and link status
  const currentTabItems = tabs[activeTab]?.items || [];
  const filteredItems = useMemo(() => {
    // Start with all current tab items
    let result = [...currentTabItems];
    
    // Apply link filter if not showing all
    if (linkFilter !== 'all') {
      result = result.filter(item => {
        if (linkFilter === 'linked') return item.isLinked;
        if (linkFilter === 'unlinked') return !item.isLinked;
        return true; // fallback
      });
    }
    
    // Then apply search filter if active
    if (!searchActive) return result;
    
    // Optimize filtering with early returns and targeted comparisons
    const searchLower = debouncedSearchQuery.toLowerCase();
    return result.filter(item => {
      if (!item) return false;
      
      // Check ID first as it's typically shorter
      const idLower = item.id?.toLowerCase() || '';
      if (idLower.includes(searchLower)) return true;
      
      // Then check name
      const nameLower = item.name?.toLowerCase() || '';
      return nameLower.includes(searchLower);
    });
  }, [currentTabItems, debouncedSearchQuery, searchActive]);

  // Set up virtualizer for efficient rendering
  const scrollRef = useRef<HTMLDivElement>(null);
  const rowVirtualizer = useVirtualizer({
    count: filteredItems.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 45, // Approximate height of each row
    overscan: 10, // Number of items to render outside of viewport
  });
  const virtualItems = rowVirtualizer.getVirtualItems();

  const handleSelectAll = (checked: boolean) => {
    const newSelected = { ...selectedItems };
    filteredItems.forEach(item => {
      newSelected[item.id] = checked;
    });
    setSelectedItems(newSelected);
  };

  const handleItemSelect = (id: string, checked: boolean) => {
    setSelectedItems(prev => ({
      ...prev,
      [id]: checked,
    }));
  };

  const hasFilteredItems = Array.isArray(filteredItems) && filteredItems.length > 0;
  const allSelected = hasFilteredItems && 
    filteredItems.every((item) => selectedItems[item.id]);
  const someSelected = hasFilteredItems && 
    filteredItems.some((item) => selectedItems[item.id]) && 
    !allSelected;

  // Effect to load assets data immediately when modal opens
  useEffect(() => {
    if (isOpen) {
      console.log('Modal opened, explicitly loading assets tab data');
      // Always force reload the assets tab when modal opens
      loadedTabsRef.current['assets'] = false;
      loadingRef.current['assets'] = false;
      // Directly call fetchTabData with explicit 'assets' tabId
      fetchTabData('assets', 0, false);
    }
  }, [isOpen, fetchTabData]);

  useEffect(() => {
    if (!isOpen) {
      // Reset all state when modal is closed
      setSelectedItems({});
      setSearchQuery('');
      setSearchActive(false);
      
      // Reset tab items to prevent memory issues on next open
      setTabs(prev => {
        const newTabs = { ...prev };
        TAB_IDS.forEach(id => {
          newTabs[id] = {
            ...newTabs[id],
            items: [],
            page: 0,
            hasMore: true
          };
        });
        return newTabs;
      });
      
      // Reset loaded tabs tracking
      TAB_IDS.forEach(id => {
        loadedTabsRef.current[id] = false;
      });
    }
  }, [isOpen]);

  // Helper function to create ComponentLink objects from selected items
  const createComponentLinks = (): ComponentLink[] => {
    const selectedIds = Object.keys(selectedItems).filter(id => id && id.trim() !== '');
    
    return selectedIds.map(id => {
      // Find the corresponding item for this ID to get the name/title
      const item = filteredItems.find(item => item.id === id);
      
      // Detect domain based on ID prefix or active tab
      const domain = detectDomainFromId(id) || getDomainForTab(activeTab);
      const relation = getRelationForDomain(domain);
      
      return {
        id: `internal:${id}`,
        domain,
        scheme: 'internal',
        ref: id,
        title: item?.name || '',
        relation,
        confidence: 0.7 // Default confidence level
      };
    });
  };
  
  // Try to detect domain from ID prefix patterns
  const detectDomainFromId = (id: string): string | null => {
    // Common prefix patterns
    if (/^AST-\d+/i.test(id)) return 'Asset';
    if (/^ASS-\d+/i.test(id) || /^ASS-\d+/i.test(id)) return 'Assurance';
    // Match any occurrence of 'ass' (case-insensitive) followed by digits, e.g., 'disp_ass001', 'ass001', 'ismass001'
    if (/ass\d+/i.test(id)) return 'Assurance';
    if (/^POL-\d+/i.test(id) || /^policy_\d+/i.test(id)) return 'Policy';
    if (/^RSK-\d+/i.test(id)) return 'Risk';
    if (/^DOC-\d+/i.test(id)) return 'Document';
    if (/^A\.\d+\.\d+/i.test(id) || /^ISO\d+/i.test(id)) return 'Control';
    
    // Try to match common patterns in IDs
    if (id.toLowerCase().includes('asset')) return 'Asset';
    if (id.toLowerCase().includes('assurance')) return 'Assurance';
    if (id.toLowerCase().includes('policy')) return 'Policy';
    if (id.toLowerCase().includes('risk')) return 'Risk';
    if (id.toLowerCase().includes('doc')) return 'Document';
    if (id.toLowerCase().includes('control')) return 'Control';
    
    // Return null if no match, so the tab-based domain will be used
    return null;
  };
  
  // Get domain based on tab type
  const getDomainForTab = (tabId: TabId): string => {
    switch (tabId) {
      case 'assets': return 'Asset';
      case 'assurances': return 'Assurance';
      case 'documents': return 'Document';
      case 'policies': return 'Policy';
      case 'risks': return 'Risk';
      case 'controls': return 'Control';
      case 'linkedComponents': return 'Other';
      default: return 'Other';
    }
  };
  
  // Get relation type based on tab
  const getRelationForTab = (tabId: TabId): string => {
    switch (tabId) {
      case 'assets': return 'impacted-by';
      case 'assurances': return 'assures';
      case 'documents': return 'supports';
      case 'policies': return 'implements';
      case 'risks': return 'mitigates';
      case 'controls': return 'maps-to';
      default: return 'maps-to';
    }
  };
  
  // Get relation type based on domain
  const getRelationForDomain = (domain: string): string => {
    switch (domain) {
      case 'Asset': return 'impacted-by';
      case 'Assurance': return 'assures';
      case 'Document': return 'supports';
      case 'Policy': return 'implements';
      case 'Risk': return 'mitigates';
      case 'Control': return 'maps-to';
      case 'Standard': return 'complies-with';
      case 'Procedure': return 'implements';
      default: return 'maps-to';
    }
  };

  const handleSubmit = async () => {
    // First check that we have valid selections
    const validItemKeys = Object.keys(selectedItems).filter(id => id && id.trim() !== '');
    
    if (validItemKeys.length === 0) {
      toast.warning(`Please select at least one item to continue.`);
      return;
    }

    console.log('Selected items:', selectedItems);
    console.log('Control IDs from ref:', selectedControlIdsRef.current);
    
    try {
      setIsSubmitting(true);
      
      // Create component links from selected items
      const links = createComponentLinks();
      console.log('Created component links:', links);
      
      // Validate links are properly formed
      if (!links || links.length === 0) {
        throw new Error('Failed to create valid component links');
      }
      
      // Use the new linkedComponents structure
      await onSubmit({
        type: 'linkedComponents', // Always use linkedComponents as the type
        action: 'add',
        data: { links }, // Pass the links array instead of itemIds
      });
      
      toast.success(`Successfully associated ${links.length} items with ${selectedCount} controls`);
      
      onClose();
    } catch (error) {
      console.error('Error performing bulk operation:', error);
      toast.error(`Failed to perform the bulk operation. Please try again.`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent 
        className="max-h-[80vh] flex flex-col bg-card" 
        style={{ maxWidth: '56rem', width: '95%' }}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Bulk Operations</DialogTitle>
          <DialogDescription>
            Associate items with {selectedCount} selected control{selectedCount !== 1 ? 's' : ''}
            <div className="mt-2 text-xs grid grid-cols-2 gap-2">
              <div className="font-medium">Domain types detected from:</div>
              <div>Item ID patterns & active tab selection</div>
              
              <div className="font-medium">Example domains:</div>
              <div className="flex flex-wrap gap-1">
                {['Asset', 'Assurance', 'Policy', 'Risk', 'Document', 'Control'].map(domain => (
                  <span key={domain} className="inline-flex items-center rounded-md bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-600/10">
                    {domain}
                  </span>
                ))}
              </div>
            </div>
          </DialogDescription>
        </DialogHeader>

        <Tabs 
          value={activeTab} 
          onValueChange={(value) => setActiveTab(value as TabId)}
          className="flex flex-col flex-1 overflow-hidden"
        >
          <TabsList className="flex w-full">
            {/* Show first 5 tabs directly */}
            {TAB_IDS.slice(0, MAX_TABS).map((id) => (
              <TabsTrigger key={id} value={id} className="flex-1">
                {getTabLabel(id)}
              </TabsTrigger>
            ))}
            
            {/* Show dropdown for additional tabs if there are more than MAX_TABS */}
            {TAB_IDS.length > MAX_TABS && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <div className="flex-none">
                    <TabsTrigger 
                      value={TAB_IDS.slice(MAX_TABS).includes(activeTab) ? activeTab : "more"} 
                      className="flex-none px-3 gap-1 h-9 transition-all data-[state=active]:bg-primary data-[state=active]:text-white"
                    >
                      {TAB_IDS.slice(MAX_TABS).includes(activeTab) ? 
                        <div className="flex items-center gap-1">
                          <span>{getTabLabel(activeTab)}</span>
                          <ChevronDown className="h-3 w-3 opacity-70" />
                        </div> : 
                        <>
                          <MoreHorizontal className="h-4 w-4" />
                          <span className="sr-only md:not-sr-only md:inline-block ml-1">More</span>
                        </>
                      }
                    </TabsTrigger>
                  </div>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-40">
                  {TAB_IDS.slice(MAX_TABS).map((id) => (
                    <DropdownMenuItem 
                      key={id}
                      onClick={() => setActiveTab(id)}
                      className={activeTab === id ? "bg-accent text-accent-foreground" : ""}
                    >
                      {getTabLabel(id)}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </TabsList>

          {TAB_IDS.map((id) => {
            const tab = tabs[id];
            return (
              <TabsContent key={id} value={id} className="space-y-4">
                <div className="space-y-2">
                  {/* Search input */}
                  <div className="relative">
                    <AlertCircle className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                    <input
                      type="search"
                      ref={id === activeTab ? searchInputRef : null}
                      placeholder={`${tab.placeholder} (type 2+ characters)`}
                      className="w-full bg-background pl-8"
                      value={searchQuery}
                      // Handle change events with special handling for clearing
                      onChange={(e) => {
                        const newValue = e.target.value;
                        setSearchQuery(newValue);
                        
                        // When user completely clears the search box, immediately respond
                        if (newValue === '' && searchQuery.length > 0) {
                          // Remember that we were focused before clearing
                          wasFocusedRef.current = true;
                          
                          // Force immediate focus
                          requestAnimationFrame(() => {
                            if (searchInputRef.current) {
                              searchInputRef.current.focus();
                            }
                          });
                        }
                      }}
                      // Include onFocus to ensure input correctly handles focus events
                      onFocus={() => {forceUpdate();}} // Force re-render on focus
                      disabled={tab.loading || !!tab.error}
                      // Set as key property to improve focus handling
                      key={`search-input-${id}-${tab.loading ? 'loading' : 'idle'}`}
                      autoComplete="off"
                    />
                    {searchQuery && searchQuery.length > 0 && searchQuery.length < 2 && (
                      <div className="text-xs text-muted-foreground mt-1 ml-2">
                        Type at least 2 characters to search
                      </div>
                    )}
                  </div>
                  
                  {/* Link status filter */}
                  <div className="flex items-center">
                    <span className="text-sm text-muted-foreground mr-2">Filter:</span>
                    <Select
                      value={linkFilter}
                      onValueChange={(value) => setLinkFilter(value as 'all' | 'linked' | 'unlinked')}
                    >
                      <SelectTrigger className="h-8 w-[180px]">
                        <SelectValue placeholder="Filter by status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All items</SelectItem>
                        <SelectItem value="linked">Linked items only</SelectItem>
                        <SelectItem value="unlinked">Unlinked items only</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {tab.error ? (
                  <div className="flex flex-col items-center justify-center py-8 text-center text-destructive">
                    <AlertTriangle className="mb-2 h-8 w-8" />
                    <p>{tab.error}</p>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="mt-2"
                      onClick={() => {
                        setTabs(prev => ({
                          ...prev,
                          [id]: { ...prev[id], error: null, items: [] }
                        }));
                      }}
                    >
                      Retry
                    </Button>
                  </div>
                ) : tab.loading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    <span>Loading {getTabLabel(id).toLowerCase()}...</span>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center space-x-2 pb-2">
                      <Checkbox
                        id={`select-all-${id}`}
                        checked={allSelected}
                        onCheckedChange={(checked) => handleSelectAll(checked === true)}
                        disabled={filteredItems.length === 0}
                      />
                      <Label htmlFor={`select-all-${id}`} className="text-sm font-medium">
                        Select all {filteredItems.length} {getTabLabel(id).toLowerCase()}
                      </Label>
                    </div>

                    <ScrollArea className="h-[300px] rounded-md border">
                      {filteredItems.length > 0 ? (
                        <div 
                          className="relative p-2" 
                          style={{ height: `${filteredItems.length * 45}px` }}
                          ref={scrollRef}
                        >
                          {virtualItems.map((virtualItem) => {
                            const item = filteredItems[virtualItem.index];
                            return item ? (
                              <div 
                                key={item.id}
                                className={`flex items-center space-x-2 ${item.isLinked ? 'bg-blue-50 dark:bg-blue-900/20 border-l-2 border-blue-500 pl-1' : ''}`}
                                style={{
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  width: '100%',
                                  height: `${virtualItem.size}px`,
                                  transform: `translateY(${virtualItem.start}px)`,
                                }}
                              >
                                <Checkbox
                                  id={`item-${item.id}`}
                                  checked={!!selectedItems[item.id]}
                                  onCheckedChange={(checked) =>
                                    handleItemSelect(item.id, checked === true)
                                  }
                                />
                                <div className="flex-1 cursor-pointer text-sm">
                                  <div className="font-medium flex items-center gap-2">
                                    {item.name}
                                    {item.isLinked && (
                                      <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900/50 dark:text-blue-300">
                                        Linked
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-xs text-muted-foreground flex items-center gap-2">
                                    <span>{item.id}</span>
                                    <span className="inline-flex items-center rounded-md bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-600/10">
                                      {(item as any).isAssuranceTask ? 'Assurance' : (detectDomainFromId(item.id) || getDomainForTab(id as TabId))}
                                    </span>
                                    {(item as any).relation && (
                                      <span className="text-xs text-muted-foreground">
                                        {(item as any).relation}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ) : null;
                          })}
                        </div>
                      ) : (
                        <div className="py-8 text-center text-sm text-muted-foreground">
                          {searchQuery && searchQuery.length < 2 ? (
                            <>Type at least 2 characters to search {getTabLabel(id).toLowerCase()}</>
                          ) : (
                            <>No {getTabLabel(id).toLowerCase()} found
                            {searchActive ? ' matching your search' : ''}.</>
                          )}
                        </div>
                      )}
                    </ScrollArea>
                    {tabs[id].hasMore && !tabs[id].loading && filteredItems.length > 0 && !searchActive && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-2 w-full"
                        onClick={() => {
                          const nextPage = (tabs[id].page || 0) + 1;
                          fetchTabData(id, nextPage, true);
                        }}
                      >
                        Load more
                      </Button>
                    )}
                  </>
                )}
              </TabsContent>
            );
          })}
        </Tabs>

        <DialogFooter className="mt-4">
          <Button 
            variant="outline" 
            onClick={onClose}
            disabled={isLoading || isSubmitting}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit}
            disabled={isLoading || isSubmitting || Object.keys(selectedItems).length === 0}
          >
            {(isLoading || isSubmitting) ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Applying...
              </>
            ) : (
              `Apply to ${selectedCount} control${selectedCount !== 1 ? 's' : ''}`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}